import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { MetaDataPractices } from 'src/app/abstraction/nomenclator.facade';
import { Practice } from 'src/app/shared/models/practice';
import { PracticeRestriction } from 'src/app/shared/models/practiceRestriction';
import { PriceList } from 'src/app/shared/models/priceList';
import { ProvisionFee } from 'src/app/shared/models/provisionFee';
import { Specialty } from 'src/app/shared/models/specialty';
import { PatchPracticeBody } from '../services/nomenclator.service';
import { BaseState } from './base.state';
import { Complexity } from '../../shared/models/complexity';
import { Pagination } from 'src/app/shared/models/pagination';
import { SpecialtyProfessionalType } from 'src/app/shared/models/specialtyProfessionalType';



@Injectable({
    providedIn: 'root',
})
export class NomenclatorState extends BaseState {

    override store = {
        practices$: new BehaviorSubject<Practice[]>(null),
        priceList$: new BehaviorSubject<PriceList[]>(null),
        practicesCount$: new BehaviorSubject<number>(null),
        practiceRestrictions$: new BehaviorSubject<PracticeRestriction[]>(null),
        metaDataPractices$: new BehaviorSubject<MetaDataPractices>(null),
        loadingGetPractices$: new BehaviorSubject<boolean>(false),
        loadingGetPracticeRestrictions$: new BehaviorSubject<boolean>(false),
        loadingCreatingPractice$: new BehaviorSubject<boolean>(false),
        loadingUpdatingPractice$: new BehaviorSubject<boolean>(false),
        loadingGetPagination$: new BehaviorSubject<boolean>(false),
        provisionFees$: new BehaviorSubject<ProvisionFee[]>(null),
        loadingGetProvisionFees$: new BehaviorSubject<boolean>(false),
        // Complexities
        loadingGetComplexities$: new BehaviorSubject<boolean>(false),
        complexities$: new BehaviorSubject<Complexity[]>(null),
        //Specialties
        specialties$: new BehaviorSubject<Specialty[]>(null),
        specialtiesPagination$: new BehaviorSubject<Pagination>(null),
        metaDataSpecialties$: new BehaviorSubject<Specialty[]>(null),
        loadingGetSpecialties$: new BehaviorSubject<boolean>(false),
        loadingCreateSpecialty$: new BehaviorSubject<boolean>(false),
        loadingUpdateSpecialty$: new BehaviorSubject<boolean>(false),
        specialtiesCount$: new BehaviorSubject<number>(null),
        loadingGettingSpecialtiesCount$: new BehaviorSubject<boolean>(false),
        specialtyProfessionalTypes$: new BehaviorSubject<SpecialtyProfessionalType[]>(null),
        loadingGettingSpecialtyProfessionalTypes$: new BehaviorSubject<boolean>(false),

    };

    constructor() {
        super();
    }

    isLoadingGetPractices$() {
        return this.store.loadingGetPractices$.asObservable();
    }

    setLoadingGetPractices(isLoadingGetPractices: boolean) {
        this.store.loadingGetPractices$.next(isLoadingGetPractices);
    }

    isLoadingGetPagination$() {
        return this.store.loadingGetPagination$.asObservable();
    }

    setLoadingGetPagination(isLoadingGettingPagination: boolean) {
        this.store.loadingGetPagination$.next(isLoadingGettingPagination);
    }

    isLoadingCreatingPractice$() {
        return this.store.loadingCreatingPractice$.asObservable();
    }

    setLoadingCreatingPractice(isLoading: boolean) {
        this.store.loadingCreatingPractice$.next(isLoading);
    }

    isLoadingUpdatingPractice$() {
        return this.store.loadingUpdatingPractice$.asObservable();
    }

    setLoadingUpdatingPractice(isLoading: boolean) {
        this.store.loadingUpdatingPractice$.next(isLoading);
    }

    getPractices$(): Observable<Practice[]> {
        return this.store.practices$.asObservable();
    }

    getPracticesCount$(): Observable<number> {
        return this.store.practicesCount$.asObservable();
    }

    setPracticesCount(count: number) {
        this.store.practicesCount$.next(count);
    }

    getMetaDataPractices$(): Observable<MetaDataPractices> {
        return this.store.metaDataPractices$.asObservable();
    }

    setMetaDataPractices(md: MetaDataPractices) {
        this.store.metaDataPractices$.next(md);
    }

    addPractice(practice: Practice) {
        this.add<Practice>({ data: practice, storeRefAttribute: this.store.practices$ })
    }

    updatePractice(practice: PatchPracticeBody, practiceId: number) {
        this.update<Partial<Practice>>({ data: practice, dataId: practiceId, storeRefAttribute: this.store.practices$ })
    }

    setPractices(practices: Practice[]) {
        this.store.practices$.next(practices);
    }

    isLoadingGetPracticeRestrictions$() {
        return this.store.loadingGetPracticeRestrictions$.asObservable();
    }

    setLoadingGetPracticeRestrictions(isLoading: boolean) {
        this.store.loadingGetPracticeRestrictions$.next(isLoading);
    }

    setPracticeRestrictions(practiceRestrictions: PracticeRestriction[]) {
        this.store.practiceRestrictions$.next(practiceRestrictions);
    }

    getPracticeRestrictions$(): Observable<PracticeRestriction[]> {
        return this.store.practiceRestrictions$.asObservable();
    }

    isLoadingGetProvisionFees() {
        return this.store.loadingGetProvisionFees$.asObservable();
    }

    setLoadingProvisionFees(isLoadingGetProvisionFees: boolean) {
        this.store.loadingGetProvisionFees$.next(isLoadingGetProvisionFees);
    }

    getProvisionFees(): Observable<ProvisionFee[]> {
        return this.store.provisionFees$.asObservable();
    }

    setProvisionFees(provisionFees: ProvisionFee[]) {
        this.store.provisionFees$.next(provisionFees);
    }

    isSupplyPractice(practiceId: number): boolean {
        return this.store.practices$.getValue()?.find(p => p.id == practiceId)?.specialty?.isSupply;
    }

    // Complexities
    setLoadingGetComplexities(isLoadingGetComplexities: boolean) {
        this.store.loadingGetComplexities$.next(isLoadingGetComplexities);
    }

    setComplexities(complexities: Complexity[]) {
        this.store.complexities$.next(complexities);
    }

    //Specialties
    getSpecialties$(): Observable<Specialty[]> {
        return this.store.specialties$.asObservable();
    }

    setSpecialties(specialties: Specialty[]) {
        this.store.specialties$.next(specialties);
    }

    getSpecialtiesPagination$(): Observable<Pagination> {
        return this.store.specialtiesPagination$.asObservable();
    }


    setSpecialtiesPagination(pagination: Pagination) {
        this.store.specialtiesPagination$.next(pagination)
    }

    setSpecialty(specialty: Specialty) {
        const previousStore = this.store.specialties$.value
        this.store.specialties$.next({ ...previousStore, ...specialty });
    }

    setLoadingGetSpecialties(value: boolean) {
        this.store.loadingGetSpecialties$.next(value);
    }

    isLoadingGetSpecialties$() {
        return this.store.loadingGetSpecialties$.asObservable();
    }

    setLoadingCreateSpecialty(value: boolean) {
        this.store.loadingCreateSpecialty$.next(value);
    }

    isLoadingCreateSpecialty$() {
        return this.store.loadingCreateSpecialty$.asObservable();
    }

    setLoadingUpdateSpecialty(value: boolean) {
        this.store.loadingUpdateSpecialty$.next(value);
    }

    isLoadingUpdateSpecialty$() {
        return this.store.loadingUpdateSpecialty$.asObservable();
    }

    isLoadingGettingSpecialtiesCount$() {
        return this.store.loadingGettingSpecialtiesCount$.asObservable();
     }

     setLoadingGettingSpecialtiesCount(loading: boolean) {
        this.store.loadingGettingSpecialtiesCount$.next(loading);
     }

     getSpecialtiesCount$(): Observable<number> {
        return this.store.specialtiesCount$.asObservable();
     }

     setSpecialtiesCount(SpecialtiesCount: number) {
        this.store.specialtiesCount$.next(SpecialtiesCount);
     }

         getSpecialtyProfessionalTypes$(): Observable<SpecialtyProfessionalType[]> {
        return this.store.specialtyProfessionalTypes$.asObservable();
    }

    setSpecialtyProfessionalTypes(specialtyProfessionalTypes: SpecialtyProfessionalType[]) {
        this.store.specialtyProfessionalTypes$.next(specialtyProfessionalTypes);
    }

    setLoadingGettingSpecialtyProfessionalTypes(loading: boolean) {
        this.store.loadingGettingSpecialtyProfessionalTypes$.next(loading);
    }

    isLoadingGettingSpecialtyProfessionalTypes$() {
        return this.store.loadingGettingSpecialtyProfessionalTypes$.asObservable();
    }
}
